import AdminInclusionTable from "@client/components/round/allocator/AdminInclusionTable";
import DisclosureInfo from "@client/components/round/DisclosureInfo";
import {
  InterestInstructions,
  RecusalInstructions,
} from "@client/components/round/Instructions";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import ProposalLink from "@client/components/utils/ProposalLink";
import { Tooltip } from "@client/components/utils/Tooltip";
import VideoPlayer from "@client/components/utils/VideoPlayer";
import DownChevron from "@client/icons/DownChevron";
import {
  getInterestDescription,
  interestBg,
  isEffectivelyRecused,
  totalColumnColor,
} from "@client/utils/interest";
import { memberIsMeanRecommender } from "@client/utils/memberIsMeanRecommender";
import {
  proposalListSubtitle,
  proposalListTitle,
} from "@client/utils/proposalListTitle";
import { RecentIndicator } from "@client/utils/recentProposals";
import { accessiblePhases } from "@client/utils/roundStatusUtils";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  assertGetMember,
  getActiveProposals,
  getActiveRecommenders,
  getAllMembers,
  getEffectiveTrack,
  hasTrackWithMatchingStatus,
  isUserFunder,
} from "@engine/round/accessors";
import type { Proposal, RoundMember } from "@engine/round/aggregate";
import type { Track } from "@lib/types";
import { sortByProperty } from "@lib/utils/sortByProperty";
import { useLocalStorage } from "@uidotdev/usehooks";
import clsx from "clsx";
import partition from "lodash/partition";
import { useContext, useState } from "react";
import { Redirect } from "wouter";
import { DispatchContext, StateContext } from "./Round";

export async function handleSpreadsheetKeydown(
  makeId: (index: number) => string,
  proposals: Proposal[],
  handleChange: (proposalId: string, value: number | null) => Promise<void>,
  e: React.KeyboardEvent,
  currentIndex: number,
) {
  const proposalId = proposals[currentIndex].id;

  switch (e.key) {
    case "ArrowUp":
      e.preventDefault();
      document.getElementById(makeId(currentIndex - 1))?.focus();
      break;
    case "Enter":
    case "ArrowDown": {
      e.preventDefault();
      document.getElementById(makeId(currentIndex + 1))?.focus();
      break;
    }
    case "Backspace": {
      e.preventDefault();
      await handleChange(proposalId, null);
      setTimeout(() => {
        document.getElementById(makeId(currentIndex))?.focus();
      }, 0);
      break;
    }
  }
}

export function partitionProposals(
  proposals: Proposal[],
  viewerTrack: Track,
): {
  inTrack: Proposal[];
  outOfTrack: Proposal[];
} {
  function matchesTrack({ typedFields }: Proposal): boolean {
    return (
      (viewerTrack === "FREEDOM" && typedFields.targetsFreedom) ||
      (viewerTrack === "FAIRNESS" && typedFields.targetsFairness) ||
      (viewerTrack === "MAIN" &&
        !typedFields.targetsFreedom &&
        !typedFields.targetsFairness)
    );
  }

  const [inTrack, outOfTrack] = partition(proposals, matchesTrack);

  return {
    inTrack: inTrack.sort(sortByProperty("shortname")),
    outOfTrack: outOfTrack.sort(sortByProperty("shortname")),
  };
}

const Onboarding = withErrorBoundary(() => {
  const context = useContext(StateContext);
  const { round, actingAsUserId: userId, mode } = context;
  const [instructionsVisible, setInstructionsVisible] = useLocalStorage(
    "instructionsVisible",
    true,
  );
  const userRoundMember = assertGetMember(round, userId);
  const effectiveTrack = getEffectiveTrack(mode, userRoundMember);
  const [selectedTrack, setSelectedTrack] = useState<Track>(
    ["MAIN", "FREEDOM", "FAIRNESS"].includes(effectiveTrack)
      ? effectiveTrack
      : "MAIN",
  );

  const { investigation, proposalDisclosure: disclosures } = round;

  const proposals = getActiveProposals(round).filter(
    (proposal) => proposal.id !== round.holdProposalId,
  );

  const otherMembersToShow: RoundMember[] = Object.values(round.members).filter(
    (member) =>
      member.id !== userId &&
      !memberIsMeanRecommender(member) &&
      member.track === selectedTrack &&
      Object.values(member.evaluators).some(
        (evaluator) => evaluator.type === "recommender",
      ),
  );

  const completeInterests = proposals.filter(
    (proposal) =>
      investigation[`${proposal.id}%${userId}`]?.interest != null ||
      disclosures[`${proposal.id}%${userId}`]?.selfRecused,
  ).length;

  const totalApplicants = proposals.length;

  const completeRecusals = proposals.filter(
    (proposal) => disclosures[`${proposal.id}%${userId}`] != null,
  ).length;

  const { inTrack, outOfTrack } = partitionProposals(proposals, selectedTrack);

  const freedomHasMembers = getAllMembers(round).some(
    (member) => member.track === "FREEDOM",
  );
  const fairnessHasMembers = getAllMembers(round).some(
    (member) => member.track === "FAIRNESS",
  );

  const isAdmin = userIsAdmin(userId, context);

  const phases = accessiblePhases("Onboarding", isUserFunder(round, userId));

  if (!isAdmin && !hasTrackWithMatchingStatus(round, userId, mode, phases)) {
    return <Redirect to="/" />;
  }

  return (
    <div className="p-4 pb-16 w-screen h-screen overflow-y-auto">
      <div className="pr-16">
        <h1 className="text-3xl font-bold mb-4">Onboarding Dashboard</h1>

        {userRoundMember.isRoundAdmin && (
          <details>
            <summary className="text-blue-500 cursor-pointer">
              Admin-only tool: who is included in the onboarding table?
            </summary>
            <AdminInclusionTable selectedTrack={selectedTrack} />
          </details>
        )}

        {effectiveTrack === "NO_TRACK" &&
          (freedomHasMembers || fairnessHasMembers) && (
            <div>
              Track:
              <select
                value={selectedTrack}
                onChange={(e) => setSelectedTrack(e.target.value as Track)}
              >
                <option value="MAIN">Main</option>
                {freedomHasMembers && <option value="FREEDOM">Freedom</option>}
                {fairnessHasMembers && (
                  <option value="FAIRNESS">Fairness</option>
                )}
              </select>
            </div>
          )}

        <button
          onClick={() => setInstructionsVisible(!instructionsVisible)}
          className="cursor-pointer"
        >
          <h2 className="text-2xl font-semibold">
            Instructions{" "}
            <DownChevron
              className={clsx(
                "inline w-5 h-5",
                instructionsVisible ? "" : "rotate-180",
              )}
            />
          </h2>
        </button>

        {instructionsVisible && (
          <>
            <VideoPlayer videoFilename="onboarding-tutorial.mp4" />
            <div className="flex flex-col xl:flex-row gap-4">
              <RecusalInstructions
                needsDisclosure={totalApplicants - completeRecusals}
                totalProposals={totalApplicants}
                showTasks={effectiveTrack !== "NO_TRACK"}
                forOnboarding={true}
              />
              <InterestInstructions
                complete={completeInterests}
                total={totalApplicants}
                showTasks={effectiveTrack !== "NO_TRACK"}
              />
            </div>
          </>
        )}
      </div>

      {inTrack.length > 0 && (
        <>
          <h2 className="text-2xl font-semibold mt-6">
            {proposalListTitle(selectedTrack, true)}
          </h2>
          <h3 className="text-lg font-semibold">
            {proposalListSubtitle(selectedTrack, true)}
          </h3>
          <InterestAndDisclosureTable
            proposals={inTrack}
            userRoundMember={userRoundMember}
            otherMembersToShow={otherMembersToShow}
            tableId="inTrack"
          />
        </>
      )}

      {outOfTrack.length > 0 && (
        <>
          <h2 className="text-2xl font-semibold mt-6">
            {proposalListTitle(selectedTrack, false)}
          </h2>
          <h3 className="text-lg font-semibold">
            {proposalListSubtitle(selectedTrack, false)}
          </h3>
          <InterestAndDisclosureTable
            proposals={outOfTrack}
            userRoundMember={userRoundMember}
            otherMembersToShow={otherMembersToShow}
            tableId="outOfTrack"
          />
        </>
      )}
    </div>
  );
});

export default Onboarding;

function InterestAndDisclosureTable({
  proposals,
  userRoundMember,
  otherMembersToShow,
  tableId,
}: {
  proposals: Proposal[];
  userRoundMember: RoundMember;
  otherMembersToShow: RoundMember[];
  tableId: string;
}) {
  const { round, actingAsUserId: userId, mode } = useContext(StateContext);
  const dispatch = useContext(DispatchContext);

  const { investigation, proposalDisclosure: disclosures } = round;

  const handleDisclosureChange = (
    proposalId: string,
    userId: string,
    disclosureInfo: {
      selfRecused: boolean;
      disclosure: string;
      hasDisclosure: boolean;
    },
  ) =>
    dispatch({
      type: "PROPOSAL_DISCLOSURE_CHANGE",
      proposalId,
      userId,
      ...disclosureInfo,
    });

  const handleInterestChange = (proposalId: string, value: number | null) =>
    dispatch({
      type: "INVESTIGATION_INTEREST_CHANGE",
      proposalId,
      memberId: userId,
      interest: value,
    });

  const userIsRecommender = getActiveRecommenders(round).find(
    (recommender) => recommender.memberId === userId,
  );

  const effectiveTrack = getEffectiveTrack(mode, userRoundMember);
  const interestEditable = userIsRecommender && effectiveTrack !== "NO_TRACK";

  const rowTotals = proposals.map((proposal) => {
    const membersToInclude = [...otherMembersToShow];
    if (interestEditable) {
      membersToInclude.push(userRoundMember);
    }
    return membersToInclude.reduce((acc, member) => {
      const interest =
        investigation[`${proposal.id}%${member.id}`]?.interest || 0;
      const disclosure = disclosures[`${proposal.id}%${member.id}`];
      const isRecused = isEffectivelyRecused(disclosure);
      return isRecused ? acc : acc + interest;
    }, 0);
  });

  const columnTotals = otherMembersToShow.map((member) => {
    return proposals.reduce((acc, proposal) => {
      const interest =
        investigation[`${proposal.id}%${member.id}`]?.interest || 0;
      const disclosure = disclosures[`${proposal.id}%${member.id}`];
      const isRecused = isEffectivelyRecused(disclosure);
      return isRecused ? acc : acc + interest;
    }, 0);
  });

  const userColumnTotal = proposals.reduce((acc, proposal) => {
    const interest = investigation[`${proposal.id}%${userId}`]?.interest || 0;
    const disclosure = disclosures[`${proposal.id}%${userId}`];
    const isRecused = isEffectivelyRecused(disclosure);
    return isRecused ? acc : acc + interest;
  }, 0);

  return (
    <div className="overflow-auto max-h-full max-w-full p4 mt-4 mb-40">
      <table className="light:bg-white dark:bg-stone-800 border-separate border-spacing-0">
        <thead className="sticky top-0 z-20">
          <tr className="light:bg-gray-100 dark:bg-stone-700">
            <th className="py-2 px-4 text-left border light:border-gray-200 dark:border-stone-700">
              Proposal
            </th>
            <th
              colSpan={2}
              className="py-2 px-4 border-r border-y light:border-gray-200 dark:border-stone-700"
            >
              Disclosure
            </th>
            <th
              colSpan={otherMembersToShow.length + (interestEditable ? 2 : 1)}
              className="py-2 px-4 border-r border-y light:border-gray-200 dark:border-stone-700"
            >
              Investigation Interest
            </th>
          </tr>
          <tr className="light:bg-white dark:bg-stone-800 border-r border-b light:border-gray-200 dark:border-stone-700">
            <th className="py-2 px-4 border-x border-b light:border-gray-200 dark:border-stone-700" />
            <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
              Disclosure
            </th>
            <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
              Self-recuse
            </th>
            {interestEditable && (
              <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
                {userRoundMember.initials}
              </th>
            )}
            {otherMembersToShow.map((member) => (
              <th
                key={member.id}
                className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700"
              >
                {member.initials}
              </th>
            ))}
            <th className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th className="border-x border-b light:border-gray-200 dark:border-stone-700" />
            <td
              colSpan={2}
              className="border-r border-b light:border-gray-200 dark:border-stone-700"
            />
            {interestEditable && (
              <td className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700">
                {userColumnTotal}
              </td>
            )}
            {columnTotals.map((total, index) => (
              <td
                key={index}
                className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700"
              >
                {total}
              </td>
            ))}
            <td // "Total" column
              className="py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700"
            />
          </tr>
          {proposals.map((proposal, index) => {
            const interest =
              investigation[`${proposal.id}%${userId}`]?.interest;
            const disclosure = disclosures[`${proposal.id}%${userId}`];
            const inputId = `${tableId}-interest-${index}`;
            const isRecused = isEffectivelyRecused(disclosure);
            const needsDisclosureResponse = disclosure == null;

            return (
              <tr
                key={proposal.id}
                className={clsx(
                  "text-sm",
                  needsDisclosureResponse &&
                    "light:bg-yellow-100 dark:bg-yellow-600",
                )}
              >
                <th
                  className={clsx(
                    "py-2 px-4 sticky left-0 text-left z-10",
                    "border-r border-b light:border-gray-200 dark:border-stone-700",
                    index === 0 && "border-l",
                    "light:bg-white dark:bg-stone-800",
                  )}
                >
                  <ProposalLink proposal={proposal}>
                    {proposal.shortname}
                    <RecentIndicator proposalId={proposal.id} />
                  </ProposalLink>
                </th>
                <DisclosureInfo
                  proposalId={proposal.id}
                  memberId={userId}
                  disclosure={disclosure}
                  handleDisclosureChange={handleDisclosureChange}
                />
                {interestEditable && (
                  <td
                    className={clsx(
                      "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                      !isRecused && interestBg(interest, true),
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Tooltip
                        title={getInterestDescription(interest, isRecused)}
                      >
                        <input
                          className="w-16 p-1 dark:border-stone-700 border border-[#444] rounded"
                          id={inputId}
                          placeholder="[0-4]"
                          type="text"
                          value={
                            isRecused ? "Rec" : interest == null ? "" : interest
                          }
                          disabled={isRecused}
                          autoComplete="off"
                          onChange={async (e) => {
                            const value = e.target.value.slice(-1);
                            const parsedValue = parseInt(value);
                            if (
                              value === "" ||
                              (parsedValue >= 0 && parsedValue <= 4)
                            ) {
                              await handleInterestChange(
                                proposal.id,
                                parsedValue,
                              );
                              setTimeout(() => {
                                document.getElementById(inputId)?.focus();
                              }, 0);
                            }
                          }}
                          onKeyDown={(e) =>
                            handleSpreadsheetKeydown(
                              (idx: number) => `${tableId}-interest-${idx}`,
                              proposals,
                              handleInterestChange,
                              e,
                              index,
                            )
                          }
                        />
                      </Tooltip>
                    </div>
                  </td>
                )}
                {otherMembersToShow.map((member) => {
                  const interest =
                    investigation[`${proposal.id}%${member.id}`]?.interest;
                  const disclosure = disclosures[`${proposal.id}%${member.id}`];
                  const isRecused = isEffectivelyRecused(disclosure);

                  return (
                    <td
                      key={member.id}
                      className={clsx(
                        "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                        !isRecused && interestBg(interest),
                      )}
                    >
                      {isRecused ? "Rec" : interest}
                    </td>
                  );
                })}
                <td
                  className={clsx(
                    "py-2 px-4 border-r border-b light:border-gray-200 dark:border-stone-700",
                    totalColumnColor(
                      rowTotals[index],
                      otherMembersToShow.length + 1,
                    ),
                  )}
                >
                  {rowTotals[index]}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
